import {
  Course,
  Status,
  Lesson,
  Milestone,
  MilestoneNode,
  BuildTypeEnum,
  ModelingExperienceEnum,
  DevExperienceEnum,
} from '@modules/learning/types/learning';
import { CourseConfig, courseConfigs } from '@modules/learning/constants/courseConfigs';
import avatarBeginnerData from '@modules/learning/data/temp/avatar-beginner.json';
import experienceBeginnerData from '@modules/learning/data/temp/experience-beginner.json';
import avatarIntermediateData from '@modules/learning/data/temp/avatar-intermediate.json';
import experienceIntermediateData from '@modules/learning/data/temp/experience-intermediate.json';

import { TTranslateFunction } from '@modules/localization/types/TranslateFunction';
import { TTreeListData } from '@modules/ui/shared/SideBarContentContext';
import { Locale } from '@rbx/intl';

export const COURSE_IDS = [
  'ExperienceBeginner',
  'ExperienceIntermediate',
  'AvatarBeginner',
  'AvatarIntermediate',
] as const;

export type CourseId = (typeof COURSE_IDS)[number];

const courses: Record<CourseId, Course> = {
  ExperienceBeginner: {
    ...experienceBeginnerData,
    isCompleted: Status.STATUS_ENUM_NOT_STARTED,
    courseId: 'ExperienceBeginner',
  },
  ExperienceIntermediate: {
    ...experienceIntermediateData,
    isCompleted: Status.STATUS_ENUM_NOT_STARTED,
    courseId: 'ExperienceIntermediate',
  },
  AvatarBeginner: {
    ...avatarBeginnerData,
    isCompleted: Status.STATUS_ENUM_NOT_STARTED,
    courseId: 'AvatarBeginner',
  },
  AvatarIntermediate: {
    ...avatarIntermediateData,
    isCompleted: Status.STATUS_ENUM_NOT_STARTED,
    courseId: 'AvatarIntermediate',
  },
};

export const getCourseByCourseId = (courseId: CourseId) => {
  return courses[courseId] ?? null;
};

const createLessonLookupMap = (): Map<string, Lesson> => {
  const lessonByPathMap = new Map<string, Lesson>();

  COURSE_IDS.forEach((courseId) => {
    const course = getCourseByCourseId(courseId);
    if (course) {
      course.milestones.forEach((milestone) => {
        milestone.lessons.forEach((lesson) => {
          lessonByPathMap.set(lesson.path, lesson);
        });
      });
    }
  });

  return lessonByPathMap;
};

const lessonLookupMap = createLessonLookupMap();

export const findLessonByPath = (lessonPath: string | null): Lesson | undefined => {
  if (!lessonPath) return undefined;
  return lessonLookupMap.get(lessonPath);
};

export const getLessonDuration = (
  lessonPath: string | null,
): { duration: number; durationUnit: string } | undefined => {
  const lesson = findLessonByPath(lessonPath);
  return lesson ? { duration: lesson.duration, durationUnit: lesson.durationUnit } : undefined;
};

const filterLessonsByStatus = (
  lessons: Lesson[],
  lessonStatuses: Record<string, Status>,
  targetStatus: Status,
) => lessons.filter((lesson: Lesson) => lessonStatuses[lesson.path] === targetStatus);

const findNextLesson = (allLessons: Lesson[], lessonStatuses: Record<string, Status>) => {
  return allLessons.find((lesson) => {
    const status = lessonStatuses[lesson.path];
    return (
      !status ||
      status === Status.STATUS_ENUM_NOT_STARTED ||
      status === Status.STATUS_ENUM_IN_PROGRESS
    );
  });
};

export const getCourseData = (
  courseConfig: CourseConfig,
  lessonStatuses: Record<string, Status>,
  userCourse?: Course,
) => {
  const template = getCourseByCourseId(courseConfig.courseId);
  if (!template) {
    return {
      milestones: 0,
      completedMilestones: 0,
      status: Status.STATUS_ENUM_NOT_STARTED,
      nextLesson: undefined,
      nextLessonPath: undefined,
    };
  }

  const allLessons = template.milestones.flatMap((m: Milestone) => m.lessons);
  const completedLessons = filterLessonsByStatus(
    allLessons,
    lessonStatuses,
    Status.STATUS_ENUM_COMPLETED,
  );
  const inProgressLessons = filterLessonsByStatus(
    allLessons,
    lessonStatuses,
    Status.STATUS_ENUM_IN_PROGRESS,
  );

  const completedMilestones = template.milestones.filter((milestone: Milestone) =>
    milestone.lessons.every(
      (lesson: Lesson) => lessonStatuses[lesson.path] === Status.STATUS_ENUM_COMPLETED,
    ),
  ).length;

  let status: Status;
  let nextLesson: string | undefined;
  let nextLessonPath: string | undefined;

  if (completedLessons.length === allLessons.length) {
    status = Status.STATUS_ENUM_COMPLETED;
  } else if (completedLessons.length > 0 || inProgressLessons.length > 0) {
    status = Status.STATUS_ENUM_IN_PROGRESS;
    const nextLessonObj = findNextLesson(allLessons, lessonStatuses);
    nextLesson = nextLessonObj?.title || 'Continue learning';
    nextLessonPath = nextLessonObj?.path;
  } else {
    status =
      userCourse?.courseId === courseConfig.courseId
        ? Status.STATUS_ENUM_RECOMMENDED
        : Status.STATUS_ENUM_NOT_STARTED;
  }

  return {
    milestones: template.milestones.length,
    completedMilestones,
    status,
    nextLesson,
    nextLessonPath,
  };
};

const getStatusPriority = (status: Status): number => {
  switch (status) {
    case Status.STATUS_ENUM_IN_PROGRESS:
      return 1;
    case Status.STATUS_ENUM_RECOMMENDED:
      return 2;
    case Status.STATUS_ENUM_NOT_STARTED:
      return 3;
    case Status.STATUS_ENUM_COMPLETED:
      return 4;
    default:
      return 5;
  }
};

export const formatStatus = (status: Status): string => {
  switch (status) {
    case Status.STATUS_ENUM_RECOMMENDED:
      return 'Label.Recommended';
    case Status.STATUS_ENUM_IN_PROGRESS:
      return 'Label.InProgress';
    case Status.STATUS_ENUM_NOT_STARTED:
      return 'Label.NotStarted';
    case Status.STATUS_ENUM_COMPLETED:
      return 'Label.Completed';
    default:
      return 'Label.NotStarted';
  }
};

export const getStatusClassName = (
  courseStatus: Status,
  classes: Record<string, string>,
): string => {
  switch (courseStatus) {
    case Status.STATUS_ENUM_IN_PROGRESS:
      return classes.statusInProgress;
    case Status.STATUS_ENUM_RECOMMENDED:
      return classes.statusRecommended;
    case Status.STATUS_ENUM_COMPLETED:
      return classes.statusCompleted;
    case Status.STATUS_ENUM_NOT_STARTED:
      return classes.statusNotStarted;
    default:
      return classes.statusNotStarted;
  }
};

export const sortedCourseConfigs = (lessonStatuses: Record<string, Status>, course?: Course) =>
  [...courseConfigs].sort((a, b) => {
    const courseDataA = getCourseData(a, lessonStatuses, course);
    const courseDataB = getCourseData(b, lessonStatuses, course);
    return getStatusPriority(courseDataA.status) - getStatusPriority(courseDataB.status);
  });

export const DEFAULT_MILESTONE_PREFIX = 'Milestone';
export const COURSE_PATH_SEGMENTS_INDEX = 2;

export const STEPPER_STYLES = {
  '& .MuiStepContent-root': {
    marginLeft: '12px',
    paddingLeft: '0px',
  },
} as const;

export const extractCourseTitle = (
  treeListData: TTreeListData,
  translate: TTranslateFunction,
): string => {
  if (!treeListData || treeListData.length === 0) {
    return '';
  }

  const firstNode = treeListData[0]?.nodes?.[0];
  if (!firstNode?.path) {
    return '';
  }

  const pathParts = firstNode.path.split('/');
  if (pathParts.length <= COURSE_PATH_SEGMENTS_INDEX) {
    return '';
  }

  const courseSlug = pathParts[COURSE_PATH_SEGMENTS_INDEX];
  const courseConfig = courseConfigs.find((config) => config.id === courseSlug);

  return courseConfig ? translate(courseConfig.titleKey) : '';
};

export const processMilestones = (treeListData: TTreeListData): MilestoneNode[] => {
  if (!treeListData) {
    return [];
  }

  const milestonesToProcess = treeListData.filter((group) => group.heading);

  return milestonesToProcess.map(
    (group, idx) =>
      ({
        ...group,
        id: `milestone-${idx}`,
        value: group.heading || `Milestone ${idx + 1}`,
        path: null,
        children: group.nodes,
        milestoneIndex: idx + 1,
        heading: group.heading,
      }) as MilestoneNode,
  );
};

export const findSelectedMilestoneIndex = (
  milestones: MilestoneNode[],
  selected: string,
): number => {
  const parentIdx = milestones.findIndex(
    (m) => m.id === selected || (m.children ?? []).some((c) => c.id === selected),
  );
  return parentIdx >= 0 ? parentIdx : 0;
};

export const getMilestoneProgressInfo = (
  milestones: MilestoneNode[],
  lessonStatuses: Record<string, Status>,
) => {
  let currentMilestone = -1;

  const isPreviousMilestoneCompleted = (
    milestoneIndex: number,
    milestonesArray: MilestoneNode[],
    statusesRecord: Record<string, Status>,
  ): boolean => {
    if (milestoneIndex < 0) return true;
    const milestone = milestonesArray[milestoneIndex];
    return (
      milestone?.children?.every((lesson) => {
        if (!lesson.path) return true;
        return statusesRecord[lesson.path] === Status.STATUS_ENUM_COMPLETED;
      }) || false
    );
  };

  for (let i = 0; i < milestones.length; i += 1) {
    const milestone = milestones[i];

    const allLessonsCompleted =
      milestone?.children?.every((lesson) => {
        if (!lesson.path) return true;
        return lessonStatuses[lesson.path] === Status.STATUS_ENUM_COMPLETED;
      }) || false;

    if (i === 0 || (i > 0 && isPreviousMilestoneCompleted(i - 1, milestones, lessonStatuses))) {
      if (!allLessonsCompleted) {
        currentMilestone = i;
        break;
      }
    } else {
      break;
    }

    if (allLessonsCompleted) {
      currentMilestone = Math.min(i + 1, milestones.length - 1);
    }
  }

  if (currentMilestone === -1) {
    currentMilestone = milestones.length - 1;
  }

  return {
    currentMilestone,
    isMilestoneCompleted: (milestoneIndex: number) => {
      const milestone = milestones[milestoneIndex];
      if (!milestone) return false;

      return (
        milestone?.children?.every((lesson) => {
          if (!lesson.path) return true;
          return lessonStatuses[lesson.path] === Status.STATUS_ENUM_COMPLETED;
        }) || false
      );
    },
    isMilestoneAccessible: (milestoneIndex: number) => {
      if (milestoneIndex === 0) return true;

      if (milestoneIndex <= currentMilestone) return true;

      return isPreviousMilestoneCompleted(milestoneIndex - 1, milestones, lessonStatuses);
    },
    isMilestoneActive: (milestoneIndex: number) => {
      return milestoneIndex === currentMilestone;
    },
  };
};

export const isMilestoneAccessible = (
  milestoneIndex: number,
  milestones: MilestoneNode[],
  lessonStatuses: Record<string, Status>,
): boolean => {
  return getMilestoneProgressInfo(milestones, lessonStatuses).isMilestoneAccessible(milestoneIndex);
};

export const isMilestoneActive = (
  milestoneIndex: number,
  milestones: MilestoneNode[],
  lessonStatuses: Record<string, Status>,
): boolean => {
  return getMilestoneProgressInfo(milestones, lessonStatuses).isMilestoneActive(milestoneIndex);
};

export const getCurrentMilestone = (
  milestones: MilestoneNode[],
  lessonStatuses: Record<string, Status>,
): number => {
  return getMilestoneProgressInfo(milestones, lessonStatuses).currentMilestone;
};

export const isMilestoneCompleted = (
  milestoneIndex: number,
  milestones: MilestoneNode[],
  lessonStatuses: Record<string, Status>,
): boolean => {
  return getMilestoneProgressInfo(milestones, lessonStatuses).isMilestoneCompleted(milestoneIndex);
};

export const getRecommendedCourse = (
  buildType: BuildTypeEnum,
  modelingExperience: ModelingExperienceEnum,
  devExperience: DevExperienceEnum,
): string => {
  if (buildType === BuildTypeEnum.BUILD_TYPE_ENUM_MODELING) {
    return modelingExperience === ModelingExperienceEnum.MODELING_EXPERIENCE_ENUM_BEGINNER
      ? 'Avatar Beginner'
      : 'Avatar Intermediate';
  }

  if (buildType === BuildTypeEnum.BUILD_TYPE_ENUM_EXPERIENCE) {
    return devExperience === DevExperienceEnum.DEV_EXPERIENCE_ENUM_BEGINNER
      ? 'Experience Beginner'
      : 'Experience Intermediate';
  }

  return 'Experience Beginner';
};

export const getCourseLinkForLocale = (locale: Locale, pathSuffix: string): string => {
  if (!pathSuffix) {
    return `/${locale.toLowerCase()}`;
  }
  const normalizedPath = pathSuffix.startsWith('/') ? pathSuffix : `/${pathSuffix}`;
  return `/${locale.toLowerCase()}${normalizedPath}`;
};
